import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Loader2, KeyRound, Mail, ArrowRight, ScanFace, Fingerprint } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useWebAuthn } from "@/hooks/useWebAuthn";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const MFAVerify = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { hasCredential, login: passkeyLogin, loading: passkeyLoading } = useWebAuthn();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState("");

  // Email OTP step (after passkey verification)
  const [showEmailOtp, setShowEmailOtp] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpCountdown, setOtpCountdown] = useState(0);

  // Manual passkey code entry
  const [showManualCode, setShowManualCode] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [manualLoading, setManualLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    loadFactor();
  }, [user]);

  useEffect(() => {
    if (otpCountdown <= 0) return;
    const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpCountdown]);

  const loadFactor = async () => {
    const { data } = await supabase.auth.mfa.listFactors();
    const verifiedFactor = data?.totp?.find((f) => f.status === "verified");
    if (verifiedFactor) {
      setFactorId(verifiedFactor.id);
    } else {
      navigate("/mfa-setup");
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.length !== 6 || !factorId) return;

    setLoading(true);
    try {
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });
      if (verifyError) throw verifyError;

      // Mark MFA as verified for 6 months
      localStorage.setItem("mfa_verified", JSON.stringify({ userId: user?.id, timestamp: Date.now() }));
      localStorage.removeItem("mfa_required");
      navigate("/wallet");
    } catch (error: any) {
      console.error("MFA verify error:", error);
      toast({
        title: t("mfa.verifyError"),
        description: t("mfa.invalidCode"),
        variant: "destructive",
      });
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  // After passkey verification passes, send email OTP
  const handlePasskeyRecovery = async () => {
    const result = await passkeyLogin();
    if (!result.success) {
      toast({
        title: "فشل التحقق بمفتاح Passkey",
        description: result.error || "لم يتم التحقق من الهوية باستخدام Passkey / بصمة الوجه",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "تم التحقق ✅", description: "تم التحقق من هويتك بواسطة Passkey" });
    setShowEmailOtp(true);
    await sendResetOtp();
  };

  // Manual passkey code (credential ID) verification
  const handleManualCodeVerify = async () => {
    if (!manualCode.trim()) return;
    setManualLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-passkey-code", {
        body: { code: manualCode.trim() },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "تم التحقق ✅", description: "تم التحقق من مفتاح Passkey" });
      setShowManualCode(false);
      setManualCode("");
      setShowEmailOtp(true);
      await sendResetOtp();
    } catch (err: any) {
      toast({
        title: "رمز غير صحيح",
        description: err.message || "الرمز غير مطابق لأي مفتاح Passkey مسجل",
        variant: "destructive",
      });
    } finally {
      setManualLoading(false);
    }
  };

  const sendResetOtp = async () => {
    setEmailOtpSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("mfa-reset-otp", {
        body: { action: "send" },
      });
      if (error) throw error;
      setMaskedEmail(data.email || user?.email || "");
      setOtpCountdown(60);
      toast({ title: "تم إرسال رمز التحقق ✉️", description: "تحقق من بريدك الإلكتروني" });
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message || "فشل إرسال الرمز", variant: "destructive" });
    } finally {
      setEmailOtpSending(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (emailOtp.length !== 6) return;
    setEmailOtpLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("mfa-reset-otp", {
        body: { action: "verify", otp: emailOtp },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Both Didit + Email OTP verified — reset MFA
      localStorage.removeItem("mfa_enroll_data");
      toast({
        title: t("mfa.resetSuccess"),
        description: t("mfa.scanNewQR"),
      });
      setShowEmailOtp(false);
      navigate("/mfa-setup");
    } catch (error: any) {
      toast({
        title: t("mfa.verifyError"),
        description: "رمز غير صحيح أو منتهي الصلاحية",
        variant: "destructive",
      });
      setEmailOtp("");
    } finally {
      setEmailOtpLoading(false);
    }
  };

  // Email OTP step UI (after Didit)
  if (showEmailOtp) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-background">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-crypto-purple/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "-3s" }} />
        </div>

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 shadow-glow mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">تأكيد البريد الإلكتروني</h1>
            <p className="text-muted-foreground mt-2">
              تم التحقق من هويتك بنجاح ✅
            </p>
            <p className="text-muted-foreground text-sm mt-1">
              أدخل رمز التحقق المرسل إلى بريدك لإتمام إعادة التعيين
            </p>
            {maskedEmail && (
              <p className="text-primary font-medium mt-1" dir="ltr">{maskedEmail}</p>
            )}
          </div>

          <div className="glass rounded-2xl p-8 shadow-card animate-scale-in">
            <div className="flex flex-col items-center space-y-6">
              <InputOTP
                maxLength={6}
                value={emailOtp}
                onChange={setEmailOtp}
                disabled={emailOtpLoading}
              >
                <InputOTPGroup className="gap-2" dir="ltr">
                  <InputOTPSlot index={0} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={1} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={2} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={3} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={4} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={5} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                </InputOTPGroup>
              </InputOTP>

              <Button
                onClick={handleVerifyEmailOtp}
                disabled={emailOtp.length !== 6 || emailOtpLoading}
                className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-lg"
              >
                {emailOtpLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    تأكيد وإعادة تعيين المصادقة
                    <ArrowRight className="w-5 h-5 mr-2" />
                  </>
                )}
              </Button>

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">لم يصلك الرمز؟</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={sendResetOtp}
                  disabled={emailOtpSending || otpCountdown > 0}
                  className="text-primary"
                >
                  {otpCountdown > 0
                    ? `إعادة الإرسال بعد ${otpCountdown} ثانية`
                    : emailOtpSending
                      ? "جاري الإرسال..."
                      : "إعادة إرسال الرمز"
                  }
                </Button>
              </div>

              <Button
                variant="link"
                onClick={() => {
                  setShowEmailOtp(false);
                  setEmailOtp("");
                }}
                className="text-muted-foreground"
              >
                العودة
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-crypto-purple/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "-3s" }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-primary shadow-glow mb-4">
            <ShieldCheck className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">{t("mfa.verifyTitle")}</h1>
          <p className="text-muted-foreground mt-2">{t("mfa.verifyDesc")}</p>
        </div>

        <div className="glass rounded-2xl p-8 shadow-card animate-scale-in">
          <form onSubmit={handleVerify} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-foreground">{t("mfa.enterCode")}</Label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="bg-input border-border text-foreground text-center text-2xl tracking-[0.5em] font-mono"
                dir="ltr"
                autoFocus
                autoComplete="one-time-code"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t("mfa.verify")
              )}
            </Button>
          </form>

          {/* Lost Authenticator - choose Face ID or manual Passkey */}
          <div className="mt-6 pt-4 border-t border-border space-y-3">
            <p className="text-xs text-muted-foreground text-center">
              {t("mfa.lostAuthenticator")}
            </p>
            {hasCredential ? (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePasskeyRecovery}
                  disabled={passkeyLoading}
                  className="flex-col h-auto py-3 gap-1"
                >
                  {passkeyLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <ScanFace className="w-5 h-5 text-primary" />
                  )}
                  <span className="text-xs">بصمة الوجه</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowManualCode(true)}
                  className="flex-col h-auto py-3 gap-1"
                >
                  <Fingerprint className="w-5 h-5 text-primary" />
                  <span className="text-xs">إدخال رمز يدوي</span>
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowManualCode(true)}
                className="w-full h-auto py-3 flex-col gap-1"
              >
                <Fingerprint className="w-5 h-5 text-primary" />
                <span className="text-xs">إدخال رمز مفتاح Passkey يدوياً</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Manual passkey code dialog */}
      <Dialog open={showManualCode} onOpenChange={setShowManualCode}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" />
              إدخال رمز مفتاح Passkey
            </DialogTitle>
            <DialogDescription>
              الصق معرّف المفتاح الذي حفظته عند إنشاء Passkey للمتابعة.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-foreground">رمز المفتاح</Label>
            <Textarea
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="الصق معرّف المفتاح هنا..."
              className="bg-input border-border text-foreground font-mono text-xs min-h-[100px] text-left"
              dir="ltr"
            />
            <Button
              onClick={handleManualCodeVerify}
              disabled={manualLoading || manualCode.trim().length < 8}
              className="w-full h-12 gradient-primary text-primary-foreground font-semibold"
            >
              {manualLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  تأكيد الرمز
                  <ArrowRight className="w-5 h-5 mr-2" />
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MFAVerify;